import { Icon, type IconName } from '@nexa/ui';

const BADGE = {
  mention: 'rounded-pill bg-brand-600 px-2 py-0.5 text-micro font-bold text-white',
  count: 'rounded-pill bg-ink-100 px-2 py-0.5 text-micro font-bold text-ink-600',
  live: 'text-nano font-extrabold tracking-label text-success-fg',
} as const;

export interface TopicListTopic {
  readonly icon: IconName;
  readonly name: string;
  readonly meta: string;
  readonly badge: { readonly kind: keyof typeof BADGE; readonly text: string };
  readonly active?: boolean;
}

export interface TopicListProps {
  readonly channel: string;
  readonly topicCount: string;
  readonly topics: readonly TopicListTopic[];
  readonly scheduled: string;
}

/**
 * Mock danh sách topic của `#collab` (`template.html` dòng 825–836).
 *
 * Ở TRONG feature, KHÔNG ở `packages/ui/src/mock/`, vì nó có ĐÚNG MỘT hộ dùng. `MockRowList` của
 * `mock/` không phủ được: thiếu icon dẫn đầu, chỉ một dòng chữ thay vì hai, badge đuôi là chữ trần
 * đổi màu theo `tone` chứ không có ba hình dạng, và hàng đang chọn ở đó chỉ đổi viền cộng bóng chứ
 * không đổi nền. Ép hai thứ vào một component sẽ đẻ 4–5 prop chỉ để bật tắt hình dạng — đó là lúc
 * component thôi là TỪ VỰNG và trở thành CẤU HÌNH. Lô C mới là hộ dùng thứ hai nếu `#knowledge`
 * cần đúng thứ này; hai hộ dùng thật mới đủ căn cứ chốt API dùng chung.
 *
 * `MockFrame` cũng không dùng: mock này không có chrome cửa sổ ba chấm, nó là card viền cộng bóng.
 *
 * `aria-hidden` ĐẶT ở đây: bốn hàng tính năng ở cột trái đã mô tả bằng chữ đúng những gì mock này
 * vẽ, nên đọc lại bằng screen reader là nhiễu. Khác với mock của `#pm` — ở đó mock LÀ nội dung.
 *
 * Ba hình dạng badge là ba THỨ khác nhau, không phải ba mức của một hình: nhắc tên (pill nền
 * brand), số chưa đọc (pill nền xám), trạng thái phòng (chữ trần). Gộp thành một hình rồi đổi màu
 * là làm sai hai trong ba.
 *
 * Cỡ chữ badge: `mention` và `count` đo được 10.5px nên vào `--text-micro` (11, gộp 10.5); `live`
 * đo được 10px nên vào `--text-nano` (9.5, gộp 10/9/8.5). Chúng là HAI token khác nhau — gán cả
 * ba vào một cỡ là lệch thấy được cạnh nhau.
 *
 * `tracking-label` (.06em) cho `live` dù thiết kế ghi `.08em`: `theme.css` không có token .08em,
 * và đây là cùng đánh đổi mà `Badge` đã ghi cho `beta-pill` — không thêm token cho một chỗ dùng.
 *
 * Màu icon theo ĐÚNG thiết kế, ba nhánh chứ không hai: `live` thắng trước, rồi hàng đang chọn dùng
 * brand-600, còn lại dùng ink-400. Thiết kế đặt ink-400 cho topic KHÔNG được chọn (dòng 834) —
 * bỏ nhánh đó đi là làm sáng một hàng lẽ ra phải mờ.
 */
export function TopicList({ channel, topicCount, topics, scheduled }: TopicListProps) {
  return (
    <div
      aria-hidden="true"
      className="rounded-card-lg border border-ink-100 bg-surface p-[18px] shadow-frame-lg"
    >
      <div className="flex items-center gap-[9px] border-b border-ink-100 pb-3.5">
        <Icon name="hash" size={15} className="text-ink-400" />
        <span className="text-body font-bold text-ink-900">{channel}</span>
        <span className="ml-auto text-eyebrow text-ink-400">{topicCount}</span>
      </div>

      <div className="mt-3.5 flex flex-col gap-[9px]">
        {topics.map((topic) => (
          <div
            key={topic.name}
            className={
              topic.active === true
                ? 'flex items-center gap-[11px] rounded-card-sm border border-brand-200 bg-brand-50 px-3.5 py-3'
                : 'flex items-center gap-[11px] rounded-card-sm border border-ink-100 px-3.5 py-3'
            }
          >
            <Icon
              name={topic.icon}
              size={15}
              className={
                topic.badge.kind === 'live'
                  ? 'text-success-fg'
                  : topic.active === true
                    ? 'text-brand-600'
                    : 'text-ink-400'
              }
            />
            <div>
              <div className="text-body-sm font-semibold text-ink-mock-900">{topic.name}</div>
              <div className="text-eyebrow text-ink-mock-500">{topic.meta}</div>
            </div>
            <span className={`ml-auto ${BADGE[topic.badge.kind]}`}>{topic.badge.text}</span>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-2.5 rounded-card-sm bg-surface-muted px-[15px] py-[13px]">
        <Icon name="clock" size={15} className="text-brand-600" />
        <span className="text-caption text-ink-700">{scheduled}</span>
      </div>
    </div>
  );
}
